import React, { useState } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EmailGuard } from "../App";
import { FaSearch, FaExclamationTriangle, FaFileInvoiceDollar, FaSpinner, FaUsers } from 'react-icons/fa';
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

// Helper function to format currency
const formatCurrency = (amount) => {
  const numericAmount = parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-KE', { 
    style: 'currency', 
    currency: 'Ksh', 
    minimumFractionDigits: 0 
  }).format(numericAmount).replace('Ksh', 'Ksh ');
};

const PendingPayments = () => {
  const [pendingData, setPendingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [propertyId, setPropertyId] = useState("");
  const [dataFetched, setDataFetched] = useState(false); // Track if fetch attempt has been made

  const fetchPendingData = async () => {
    // FIX: Only check for empty string, since Property ID can contain letters/special characters.
    if (!propertyId.trim()) { 
      toast.error("Please enter a Property ID.");
      setPendingData([]);
      setDataFetched(true);
      return;
    }

    setLoading(true);
    setPendingData([]); // Clear old data
    setDataFetched(false); // Reset fetch status
    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/admin/bill/viewabillandbalances/${propertyId.trim()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const responseData = response.data.response;
      // Handle cases where the response might be null, empty string, or not an array
      if (Array.isArray(responseData) && responseData.length > 0) {
        setPendingData(responseData);
        toast.success(`Found ${responseData.length} pending payment records for property ${propertyId.trim()}.`);
      } else {
        setPendingData([]);
        toast.info(`No pending payments found for Property ID: ${propertyId.trim()}.`);
      }
      
      setDataFetched(true);

    } catch (error) {
      console.error("Error fetching data:", error);
      const errorMessage = error.response?.data?.response || "Failed to fetch data. Check the Property ID.";
      toast.error(errorMessage);
      setPendingData([]);
      setDataFetched(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyIdChange = (e) => {
    // FIX: Allow all characters (including letters) for the Property ID.
    setPropertyId(e.target.value);
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
          
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center mb-8 border-b pb-4">
                <FaFileInvoiceDollar className="mr-3 text-red-600" /> Pending Payments & Balances
            </h1>

            {/* Input and Search Section */}
            <div className="bg-white shadow-xl rounded-xl p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <FaSearch className="mr-2 w-5 h-5 text-indigo-500" /> Search by Property
                </h2>
                <div className="flex flex-col sm:flex-row gap-4">
                    <input
                        type="text" // Ensure type is text to accept letters
                        value={propertyId}
                        onChange={handlePropertyIdChange}
                        placeholder="Enter Property ID (e.g., A101 or 123)"
                        className="flex-grow border border-gray-300 px-4 py-3 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 shadow-sm transition duration-150"
                        aria-label="Property ID Input"
                    />
                    <button
                        onClick={fetchPendingData}
                        className="bg-red-600 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-red-700 transition duration-300 ease-in-out disabled:opacity-50 flex items-center justify-center"
                        disabled={loading || !propertyId.trim()}
                    >
                        {loading ? (
                            <>
                                <FaSpinner className="animate-spin mr-2" />
                                Searching...
                            </>
                        ) : (
                            <>
                                <FaSearch className="mr-2" />
                                Fetch Balances
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Data Display Section */}
            <div className="bg-white shadow-xl rounded-xl overflow-hidden">
                {loading ? (
                    <div className="flex flex-col items-center justify-center p-12">
                        <Oval color="#ef4444" secondaryColor="#fca5a5" height={50} width={50} ariaLabel="loading-spinner" />
                        <p className="mt-4 text-gray-600">Loading payment data for Property ID: {propertyId}...</p>
                    </div>
                ) : dataFetched && pendingData.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-100">
                                        Tenant / House
                                    </th>
                                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Phone
                                    </th>
                                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Water Bill
                                    </th>
                                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rent Bill
                                    </th>
                                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Trash Bill
                                    </th>
                                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-700 uppercase tracking-wider font-bold">
                                        Billable Amount
                                    </th>
                                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-700 uppercase tracking-wider font-bold">
                                        Amount Paid
                                    </th>
                                    <th className="py-3 px-6 text-right text-sm font-extrabold text-red-600 uppercase tracking-wider">
                                        Balance
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {pendingData.map((pending, index) => (
                                    <tr key={index} className="hover:bg-red-50 transition duration-150">
                                        <td className="py-3 px-6 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                                            <div className="font-semibold">{pending.user_name || 'N/A'}</div>
                                            <div className="text-gray-500 text-xs">House {pending.house_no}</div>
                                        </td>
                                        <td className="py-3 px-6 whitespace-nowrap text-sm text-gray-500">
                                            {pending.phone}
                                        </td>
                                        <td className="py-3 px-6 whitespace-nowrap text-sm text-gray-500 text-right">
                                            {formatCurrency(pending.water_bill)}
                                        </td>
                                        <td className="py-3 px-6 whitespace-nowrap text-sm text-gray-500 text-right">
                                            {formatCurrency(pending.rent_bill)}
                                        </td>
                                        <td className="py-3 px-6 whitespace-nowrap text-sm text-gray-500 text-right">
                                            {formatCurrency(pending.trashbill)}
                                        </td>
                                        <td className="py-3 px-6 whitespace-nowrap text-sm font-semibold text-gray-800 text-right">
                                            {formatCurrency(pending.total_amount)}
                                        </td>
                                        <td className="py-3 px-6 whitespace-nowrap text-sm font-semibold text-green-600 text-right">
                                            {formatCurrency(pending.amount_paid)}
                                        </td>
                                        <td className="py-3 px-6 whitespace-nowrap text-base font-bold text-red-600 text-right">
                                            {formatCurrency(pending.balance)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : dataFetched && pendingData.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        <FaExclamationTriangle className="w-10 h-10 mx-auto mb-3 text-yellow-500" />
                        <p className="text-lg font-medium">No Pending Payments Found</p>
                        <p className="text-sm">Please check the **Property ID** and try again, or there are no outstanding balances for this property.</p>
                    </div>
                ) : (
                    <div className="p-12 text-center text-gray-400">
                        <FaUsers className="w-10 h-10 mx-auto mb-3" />
                        <p className="text-lg font-medium">Enter a **Property ID** to view pending balances.</p>
                    </div>
                )}
            </div>
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default PendingPayments;